'use strict';

// Trial-countdown math (Phase 1, 2026-08-17 — "N days left in trial" badge
// shows wrong value). Single canonical implementation, required directly by
// functions/index.js (CommonJS, Node 20) and imported by src/ (webpack
// consumes CJS module.exports via its default interop — no config needed),
// same pattern as shared/wage-engine and shared/public-holidays.
//
// Root cause this replaces (see the 17 Aug 2026 recon report): FOUR
// hand-duplicated copies of this calculation existed (Navbar.vue,
// OrgSettings.vue, router/index.js, ExpiredAccount.vue), disagreeing with
// each other (three used a hardcoded 14-day window, one used 30), computed
// against a SIGNUP-date field (`trial`, a DD/MM/YYYY string) that had also
// been hand-set on at least one org as if it meant an END date instead, and
// used `dayjs().diff(date, 'day')` — which truncates toward zero on the
// fractional-day distance between a real "now" (real time-of-day) and a
// midnight-anchored date — silently swallowing up to a whole day depending
// on what time of day the badge happened to render.
//
// Fix shape: one field (`trialEndsAt`, a Firestore Timestamp — an
// unambiguous END instant, no more signup-date-vs-end-date confusion), one
// constant (TRIAL_DAYS), and day-count arithmetic that compares
// startOf('day') anchors on BOTH sides (never a bare instant against a
// midnight anchor), so time-of-day can never shift the answer.
//
// trialEndsAt is the ONLY source of truth (as of the change that removed
// legacyTrialDaysLeft) — every org either has it (written at signup, or by
// scripts/backfill-trial-ends-at.mjs for pre-existing orgs) or this module
// returns null and callers render nothing/"Account expired". There is no
// fallback path and no parsing of the legacy `trial` string anywhere in
// this module any more.

const dayjs = require('dayjs');

const TRIAL_DAYS = 14;

// Firestore Timestamp (both `firebase/firestore` client SDK and
// `firebase-admin/firestore` Admin SDK expose `.toDate()`), a JS Date, or a
// dayjs instance are all accepted — normalised here so callers on either
// side of the client/server boundary never have to special-case the type.
// Anything else (including a raw string — deliberately, see module header)
// is treated as unparseable and returns null; this function must never
// throw and must never guess.
function normaliseToDayjs(value) {
  if (value === null || value === undefined) return null;
  if (dayjs.isDayjs(value)) {
    return value.isValid() ? value : null;
  }
  if (value instanceof Date) {
    const d = dayjs(value);
    return d.isValid() ? d : null;
  }
  if (typeof value.toDate === 'function') {
    const d = dayjs(value.toDate());
    return d.isValid() ? d : null;
  }
  return null;
}

// startDate: Firestore Timestamp / Date / dayjs instance (the signup
// instant). Returns a JS Date, TRIAL_DAYS calendar days after startDate —
// suitable for `admin.firestore.Timestamp.fromDate(...)` at the write site.
function computeTrialEnd(startDate) {
  const start = normaliseToDayjs(startDate);
  if (!start) return null;
  return start.add(TRIAL_DAYS, 'day').toDate();
}

// The only day-count arithmetic in the codebase. Both sides are reduced to
// startOf('day') before diffing — this is what makes the result immune to
// time-of-day (the bug that turned a genuine 15-day gap into -14, and then
// 28) and to DST (dayjs's 'day'-unit diff is calendar-aware, not a raw
// ms/86400000 division — verified against the Sydney Oct 2026 DST
// transition, see index.test.js).
//
// Contract:
//   > 0  -> that many whole days remain
//   0    -> expired (trialEndsAt's calendar day has arrived). Every call
//     site blocks at <= 0 (router/index.js's guard, and every "> 0 show
//     countdown / else show 'Account expired'" template) — 0 does NOT mean
//     access is still permitted for the rest of that day.
//   < 0  -> expired
//   trialEndsAt missing/unparseable -> null, NEVER a number. Callers must
//     render nothing (or "Account expired") on null — do not substitute a
//     default. A plausible-looking substituted number is exactly what let
//     this bug go undetected.
function trialDaysLeft(trialEndsAt, now) {
  const end = normaliseToDayjs(trialEndsAt);
  if (!end) return null;
  const today = normaliseToDayjs(now);
  if (!today) return null;
  return end.startOf('day').diff(today.startOf('day'), 'day');
}

module.exports = {
  TRIAL_DAYS,
  computeTrialEnd,
  trialDaysLeft,
  _internal: { normaliseToDayjs },
};
